import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDrop } from 'react-dnd';
import { format, differenceInCalendarDays, parseISO, addDays as addDaysFn } from 'date-fns';
import { updatePlan, savePlan, actions } from '../../store/slice';
import { selectPrograms } from '../../store/selectors';
import { DRAG_TYPES } from './constants';
import ProgramBar from './ProgramBar';
import { splitProgramByYears, stackPrograms } from './programUtils';

function ChannelRow({
  channel,
  programs,
  days,
  headerData,
  timelineWidth,
  programCellWidth,
  headerCellWidth,
  onProgramDrop,
  onProgramEdit,
  sidebarWidth = 120,
  onDragPreviewChange,
}) {
  const dispatch = useDispatch();
  const allProgramsFromStore = useSelector(selectPrograms);
  const [dragPreview, setDragPreview] = useState(null);

  const viewMode = headerData[0]?.viewMode || 'day';
  const effectiveCellWidth = (viewMode === 'year-multi' || viewMode === 'month')
    ? headerCellWidth
    : programCellWidth;

  // Drop disabled for Planner Dashboard (view-only mode)
  const [{ isOver }, drop] = useDrop(() => ({
    accept: [], // Empty accept array disables all drops
    canDrop: () => false, // Explicitly disable dropping
    hover: (item, monitor) => {
      // No-op - drops are disabled
      if (item.isDraggableItem) {
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;
        const cellWidth = effectiveCellWidth;
        const timelineHeader = document.querySelector('[data-timeline-header]') ||
          document.querySelector('.timeline-header') ||
          document.querySelector('div[style*="position: relative"][style*="height: 56"]');

        if (!timelineHeader) {
          const dateCells = document.querySelectorAll('div[style*="width: 40px"]');
          if (dateCells.length > 0) {
            const firstCell = dateCells[0];
            const parent = firstCell.parentElement;
            if (parent) {
              const parentRect = parent.getBoundingClientRect();
              const relativeX = clientOffset.x - parentRect.left;
              const dayIndex = Math.max(0, Math.min(headerData.length - 1, Math.floor(relativeX / cellWidth)));
              if (headerData[dayIndex] && days[dayIndex]) {
                const targetDate = new Date(days[dayIndex].date);
                const previewDate = new Date(targetDate);
                const previewEndDate = new Date(targetDate.getTime() + 24 * 60 * 60 * 1000);
                setDragPreview({
                  id: item.id,
                  since: previewDate.toISOString(),
                  till: previewEndDate.toISOString(),
                  title: item.title || 'New Task',
                  isDraggableItem: true,
                  left: dayIndex * cellWidth,
                  width: cellWidth - 8,
                });
              }
            }
          }
          return;
        }

        const containerRect = timelineHeader.getBoundingClientRect();
        const relativeX = clientOffset.x - containerRect.left;
        const dayIndex = Math.max(0, Math.min(headerData.length - 1, Math.floor(relativeX / cellWidth)));
        if (headerData[dayIndex] && days[dayIndex]) {
          const targetDate = new Date(days[dayIndex].date);
          const previewDate = new Date(targetDate);
          const previewEndDate = new Date(targetDate.getTime() + 24 * 60 * 60 * 1000);
          setDragPreview({
            id: item.id,
            since: previewDate.toISOString(),
            till: previewEndDate.toISOString(),
            title: item.title || 'New Task',
            isDraggableItem: true,
            left: dayIndex * cellWidth,
            width: Math.max(cellWidth * 2, 120),
          });
        }
        return;
      }

      const delta = monitor.getDifferenceFromInitialOffset();
      if (!delta) return;
      const cellWidth = effectiveCellWidth;
      const rawShiftDays = delta.x / cellWidth;
      const shiftDays = Math.round(rawShiftDays);
      const currentProgram = allProgramsFromStore.find(p => p.id === item.id);
      const currentSince = currentProgram ? currentProgram.since : item.since;
      const currentTill = currentProgram ? currentProgram.till : item.till;
      if (!currentSince || !currentTill) return;

      let previewSince, previewTill;
      if (item.resizeType === 'left') {
        const originalSince = parseISO(currentSince);
        const originalTill = parseISO(currentTill);
        previewSince = addDaysFn(originalSince, shiftDays);
        previewTill = originalTill;
        if (previewSince >= previewTill) previewSince = addDaysFn(previewTill, -1);
      } else if (item.resizeType === 'right') {
        const originalSince = parseISO(currentSince);
        const originalTill = parseISO(currentTill);
        previewSince = originalSince;
        previewTill = addDaysFn(originalTill, shiftDays);
        if (previewTill <= previewSince) previewTill = addDaysFn(previewSince, 1);
      } else {
        previewSince = addDaysFn(parseISO(currentSince), shiftDays);
        previewTill = addDaysFn(parseISO(currentTill), shiftDays);
      }

      const leftDays = previewSince && days[0]?.iso ? differenceInCalendarDays(previewSince, parseISO(days[0].iso)) : 0;
      const durationDays = Math.max(1, differenceInCalendarDays(previewTill, previewSince) + 1);
      const targetDayIndex = Math.max(0, Math.min(headerData.length - 1, leftDays));
      const preview = {
        left: leftDays * effectiveCellWidth,
        width: durationDays * effectiveCellWidth - 8,
        since: format(previewSince, 'dd.MM'),
        till: format(previewTill, 'dd.MM'),
        resizeType: item.resizeType,
        shiftDays: shiftDays,
        targetDay: targetDayIndex,
      };
      setDragPreview(preview);
      if (onDragPreviewChange) onDragPreviewChange(preview);
    },
    drop: (item, monitor) => {
      // Drop disabled for Planner Dashboard (view-only mode)
      return;
      try {
        if (item.isDraggableItem) {
          const clientOffset = monitor.getClientOffset();
          if (!clientOffset) return;
          const timelineHeader = document.querySelector('[data-timeline-header]') ||
            document.querySelector('.timeline-header') ||
            document.querySelector('div[style*="position: relative"][style*="height: 56"]');
          if (!timelineHeader) return;
          const containerRect = timelineHeader.getBoundingClientRect();
          const relativeX = clientOffset.x - containerRect.left;
          const cellWidth = effectiveCellWidth;
          const dayIndex = Math.max(0, Math.min(headerData.length - 1, Math.floor(relativeX / cellWidth)));
          if (headerData[dayIndex] && days[dayIndex]) {
            const targetDate = new Date(days[dayIndex].date);
            const dropDate = new Date(targetDate);
            const endDate = new Date(targetDate.getTime() + 24 * 60 * 60 * 1000);
            const newProgramData = {
              title: item.title || 'New Task',
              description: item.description || '',
              workTicketNumber: item.id,
              since: dropDate.toISOString(),
              till: endDate.toISOString(),
              channelUuid: channel.uuid || channel.id || channel.assetNumber || channel.title,
              quantity: '1',
              status: 'Open',
            };
            dispatch(savePlan(newProgramData));
            dispatch(actions.removeElement(item.id));
          }
          return;
        }

        const delta = monitor.getDifferenceFromInitialOffset();
        if (!delta) return;
        if (!item.id || !item.channelUuid || !item.since || !item.till) return;
        const cellWidth = effectiveCellWidth;
        const shiftDays = Math.round(delta.x / cellWidth);
        const currentProgram = allProgramsFromStore.find(p => p.id === item.id);
        const currentSince = currentProgram ? currentProgram.since : item.since;
        const currentTill = currentProgram ? currentProgram.till : item.till;
        if (!currentSince || !currentTill) return;

        const newChannelUuid = channel.uuid || channel.id || channel.assetNumber || channel.title;
        const machineChanged = item.channelUuid !== newChannelUuid;
        if (Math.abs(shiftDays) === 0 && !machineChanged) return;

        let since, till;
        if (item.resizeType === 'left') {
          const originalSince = parseISO(currentSince);
          const originalTill = parseISO(currentTill);
          since = addDaysFn(originalSince, shiftDays);
          till = originalTill;
          if (since >= till) since = addDaysFn(till, -1);
        } else if (item.resizeType === 'right') {
          const originalSince = parseISO(currentSince);
          const originalTill = parseISO(currentTill);
          since = originalSince;
          till = addDaysFn(originalTill, shiftDays);
          if (till <= since) till = addDaysFn(since, 1);
        } else {
          since = addDaysFn(parseISO(currentSince), shiftDays);
          till = addDaysFn(parseISO(currentTill), shiftDays);
        }

        const originalProgram = allProgramsFromStore.find(p => p.id === item.id);
        if (!originalProgram) return;

        const title = originalProgram.title || originalProgram.workTicketNumber || originalProgram.workTicketNo || 'Untitled';
        const formattedSince = format(since, "yyyy-MM-dd'T'HH:mm:ss");
        const formattedTill = format(till, "yyyy-MM-dd'T'HH:mm:ss");
        const payload = {
          ...originalProgram,
          id: item.id,
          channelUuid: newChannelUuid,
          machineId: newChannelUuid,
          since: formattedSince,
          till: formattedTill,
          title: title,
          description: originalProgram.description || '',
          userId: localStorage.getItem('UserId') || originalProgram.userId,
        };
        if (onProgramDrop) onProgramDrop(payload);
        dispatch(updatePlan(payload)).unwrap().catch(() => {});
      } finally {
        setDragPreview(null);
        if (onDragPreviewChange) onDragPreviewChange(null);
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  }), [allProgramsFromStore, headerData, timelineWidth, days, onProgramDrop, onDragPreviewChange, channel, programCellWidth, headerCellWidth, effectiveCellWidth]);

  const { items } = useMemo(() => {
    // Deduplicate programs by their id/work-ticket identifiers to avoid rendering duplicates per channel
    const uniqueMap = new Map();
    programs.forEach(p => {
      const key =
        p?.id ||
        p?.originalId ||
        p?.workTicketNumber ||
        p?.workTicketNo ||
        p?.workTicket ||
        p?.processIdExt;
      if (key) {
        if (!uniqueMap.has(key)) {
          uniqueMap.set(key, p);
        }
      } else {
        // fallback: use reference
        uniqueMap.set(p, p);
      }
    });
    const uniquePrograms = Array.from(uniqueMap.values());

    const sortedPrograms = uniquePrograms.sort((a, b) => {
      const aSince = new Date(a.since);
      const aTill = new Date(a.till);
      const bSince = new Date(b.since);
      const bTill = new Date(b.till);
      const aYearsSpanned = aTill.getFullYear() - aSince.getFullYear() + 1;
      const bYearsSpanned = bTill.getFullYear() - bSince.getFullYear() + 1;
      if (aYearsSpanned !== bYearsSpanned) return aYearsSpanned - bYearsSpanned;
      return aSince - bSince;
    });
    const splitPrograms = [];
    sortedPrograms.forEach(p => {
      const segments = splitProgramByYears(p, headerData, viewMode);
      splitPrograms.push(...segments);
    });
    return stackPrograms(splitPrograms, headerData);
  }, [programs, headerData, viewMode]);

  const cellWidth = effectiveCellWidth;

  return (
    <div style={{ display: 'flex', alignItems: 'stretch', borderBottom: '1px solid #e0e0e0' }}>
      <div
        style={{
          width: sidebarWidth,
          padding: '8px 8px',
          background: isOver ? '#e3f2fd' : '#f9fbff',
          borderRight: '1px solid #e0e0e0',
          textAlign: 'right',
          transition: 'background-color 0.2s ease',
        }}
      >
        <div style={{ fontWeight: isOver ? 700 : 600, color: isOver ? '#1565c0' : '#1976d2', transition: 'all 0.2s ease' }}>
          {channel.title || channel.assetNumber || channel.uuid}
        </div>
      </div>
      <div
        ref={drop}
        style={{
          position: 'relative',
          overflow: 'hidden',
          flex: 1,
          minHeight: 56,
          background: isOver ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
          transition: 'background-color 0.2s ease',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {headerData.map((d, i) => (
            <div
              key={d.iso || d.value}
              style={{
                position: 'absolute',
                left: i * effectiveCellWidth,
                width: effectiveCellWidth,
                top: 0,
                bottom: 0,
                borderRight: '1px solid #e0e0e0',
              }}
            />
          ))}
        </div>

        {(() => {
          const segmentGroups = new Map();
          items.forEach(p => {
            if (p.isSegment && p.originalId) {
              if (!segmentGroups.has(p.originalId)) {
                segmentGroups.set(p.originalId, []);
              }
              segmentGroups.get(p.originalId).push(p);
            }
          });
          const segmentWidths = new Map();
          segmentGroups.forEach((segments, originalId) => {
            if (segments.length > 1) {
              const sortedSegments = [...segments].sort((a, b) => new Date(a.since) - new Date(b.since));
              const firstSegment = sortedSegments[0];
              const lastSegment = sortedSegments[sortedSegments.length - 1];
              const startIsoForCalc = (headerData[0]?.viewMode === 'year-multi' || headerData[0]?.viewMode === 'month')
                ? (headerData[0]?.iso ? headerData[0].iso + 'T00:00:00' : new Date().toISOString())
                : (days[0]?.iso ? days[0].iso + 'T00:00:00' : new Date().toISOString());
              const startDate = parseISO(startIsoForCalc);
              const firstStart = parseISO(firstSegment.since);
              const lastEnd = parseISO(lastSegment.till);
              const firstLeftDays = differenceInCalendarDays(firstStart, startDate);
              const lastRightDays = differenceInCalendarDays(lastEnd, startDate) + 1;
              const totalWidth = (lastRightDays - firstLeftDays) * cellWidth;
              segmentWidths.set(originalId, totalWidth);
            }
          });

          return items.map(p => {
            if (!p.id || !p.since || !p.till) return null;
            const startIsoForBar = (headerData[0]?.viewMode === 'year-multi' || headerData[0]?.viewMode === 'month')
              ? (headerData[0]?.iso ? headerData[0].iso + 'T00:00:00' : new Date().toISOString())
              : (days[0]?.iso ? days[0].iso + 'T00:00:00' : new Date().toISOString());
            const totalSegmentWidth = (p.isSegment && p.isFirstSegment && p.originalId && segmentWidths.has(p.originalId))
              ? segmentWidths.get(p.originalId)
              : null;
            return (
              <div key={p.id} style={{ position: 'absolute', top: 6 }}>
                <ProgramBar
                  program={p}
                  cellWidth={cellWidth}
                  startIso={startIsoForBar}
                  onDoubleClick={() => onProgramEdit?.(p.originalId || p.id)}
                  viewMode={headerData[0]?.viewMode || 'day'}
                  headerData={headerData}
                  horizontalIdx={p.horizontalIdx || 0}
                  overlapCount={p.overlapCount || 1}
                  totalSegmentWidth={totalSegmentWidth}
                />
              </div>
            );
          });
        })()}

        {dragPreview && isOver && (
          <div
            style={{
              position: 'absolute',
              left: dragPreview.left,
              width: dragPreview.width,
              height: 46,
              top: 6,
              background: 'rgba(25, 118, 210, 0.3)',
              border: '2px dashed #1976d2',
              borderRadius: 8,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: '6px 10px',
              zIndex: 20,
              pointerEvents: 'none',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
            }}
          >
            <div style={{ fontWeight: 600, color: '#1976d2', fontSize: 13, lineHeight: 1.1, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
              {dragPreview.resizeType === 'left' ? '← Resize Start' : dragPreview.resizeType === 'right' ? 'Resize End →' : 'Moving...'}
            </div>
            <div style={{ color: '#1976d2', fontSize: 12 }}>
              {dragPreview.since} - {dragPreview.till}
              <span style={{ color: '#666', fontSize: 10, marginLeft: 4 }}>
                ({dragPreview.shiftDays > 0 ? '+' : ''}{dragPreview.shiftDays} days)
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChannelRow;


